import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, AlertTriangle, ExternalLink, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Danger-zone row that lets the current user permanently delete their SolarFlow
 * account. Web port of the mobile DeleteAccountRow.tsx.
 *
 * Flow: click row → modal opens → user reads impact bullets, taps Privacy/Terms
 * if needed, types "DELETE" to arm the destructive button, then hits Delete
 * forever. On success we log out and go home.
 */
export function DeleteAccountRow({ testid = "delete-account" }) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const isCustomer = user?.role === "customer";
    const armed = confirmText.trim().toUpperCase() === "DELETE";

    const close = () => {
        if (busy) return;
        setOpen(false);
        setConfirmText("");
        setErr("");
    };

    const perform = async () => {
        if (!armed) {
            setErr('Type "DELETE" to confirm.');
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await api.deleteAccount();
            logout();
            navigate("/", { replace: true });
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message || "Could not delete account. Please try again.");
            setBusy(false);
        }
    };

    return (
        <>
            <button
                data-testid={testid}
                onClick={() => setOpen(true)}
                className="w-full flex items-center gap-3 p-4 bg-white rounded-[16px] border border-[#E5E7EB] hover:border-[#DA3535]/25 transition-colors text-left"
            >
                <span className="w-9 h-9 rounded-full bg-[#FEECEC] flex items-center justify-center shrink-0">
                    <Trash2 size={18} strokeWidth={1.75} className="text-[#DA3535]" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-base text-[#DA3535] font-medium">Delete account</div>
                    <div className="text-xs text-[#4B5565] mt-0.5">Permanently remove your Pearl account and data.</div>
                </div>
                <ChevronRight size={16} className="text-[#4B5565]" />
            </button>

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-modal">
                    <div className="absolute inset-0 bg-black/55" onClick={close} />
                    <div className="relative bg-white rounded-[20px] p-6 md:p-7 w-full max-w-md shadow-2xl">
                        <div className="w-11 h-11 rounded-full bg-[#FEECEC] flex items-center justify-center">
                            <AlertTriangle size={22} strokeWidth={1.75} className="text-[#DA3535]" />
                        </div>
                        <h2 className="mt-4 text-xl text-[#131B2F] font-medium">Delete your account?</h2>
                        <p className="mt-2 text-sm text-[#4B5565] leading-relaxed">
                            {isCustomer
                                ? "Your account, project, documents, appointments and messages will be permanently deleted. This cannot be undone."
                                : "Your engineer account will be permanently deleted. This cannot be undone."}
                        </p>

                        <ul className="mt-3 space-y-1.5">
                            {(isCustomer
                                ? ["Your homeowner profile", "Installation project & phase history", "Documents you uploaded or received", "Appointments & scheduled visits", "All chat messages with your installer"]
                                : ["Your engineer sign-in", "You will no longer be able to view customer projects on this device", "Customer projects and files are retained by the business"]
                            ).map((t, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-[#4B5565] shrink-0" />
                                    <span className="text-xs text-[#131B2F] leading-relaxed">{t}</span>
                                </li>
                            ))}
                        </ul>

                        <label className="mt-4 block text-xs text-[#4B5565]">Type DELETE to confirm</label>
                        <input
                            data-testid="delete-confirm-input"
                            value={confirmText}
                            onChange={(e) => { setConfirmText(e.target.value); if (err) setErr(""); }}
                            placeholder="DELETE"
                            autoCapitalize="characters"
                            autoCorrect="off"
                            className="mt-1 w-full h-11 px-3.5 rounded-md border border-[#E5E7EB] bg-white text-base text-[#131B2F] tracking-[0.15em] outline-none focus:border-[#DA3535]"
                        />
                        {!!err && <div className="mt-1.5 text-xs text-[#DA3535]" data-testid="delete-error">{err}</div>}

                        <div className="mt-3 text-xs text-[#4B5565]">Before continuing, please review:</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <Link
                                to="/legal/privacy"
                                data-testid="delete-open-privacy"
                                onClick={close}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3E9E2E]/8 border border-[#3E9E2E]/25 text-xs text-[#3E9E2E] font-medium hover:bg-[#3E9E2E]/16 transition-colors"
                            >
                                Privacy Policy <ExternalLink size={12} strokeWidth={1.75} />
                            </Link>
                            <Link
                                to="/legal/terms"
                                data-testid="delete-open-terms"
                                onClick={close}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3E9E2E]/8 border border-[#3E9E2E]/25 text-xs text-[#3E9E2E] font-medium hover:bg-[#3E9E2E]/16 transition-colors"
                            >
                                Terms of Use <ExternalLink size={12} strokeWidth={1.75} />
                            </Link>
                        </div>

                        <div className="mt-5 flex gap-3">
                            <button
                                data-testid="delete-cancel"
                                onClick={close}
                                disabled={busy}
                                className="flex-1 h-11 rounded-md border border-[#E5E7EB] bg-white text-base text-[#131B2F] font-medium hover:bg-[#F5F5F7] transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="delete-confirm-btn"
                                onClick={perform}
                                disabled={busy || !armed}
                                className="flex-1 h-11 rounded-md bg-[#DA3535] text-white text-base font-medium hover:bg-[#B62828] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? "Deleting…" : (<><Trash2 size={16} strokeWidth={2} /> Delete forever</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
